/* eslint-disable max-lines */
// Please don't remove the above until this is resolved: https://jira.tools.aws.vodafone.com/browse/UC-31447
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Button from "@vfuk/core-button";
import SimpleGrid from "@vfuk/core-simple-grid";
import SmallButton from "@vfuk/core-small-button";
import Heading from "@vfuk/core-heading";
import Spacing from "@vfuk/core-spacing";
import LinkWithIcon from "@vfuk/core-link-with-icon";
import { useRoleManagementStore } from "src/hooks/stores";
import {
  SuccessModalContent,
  FailureModalContent,
  AccordionBox,
  Modal,
  FloatingLabelInput,
} from "@ucchub/ui";
import DeleteRoleModal from "@pages/RoleManagement/DeleteRoleModal/DeleteRoleModal";
import { useTranslation } from "react-i18next";
import type { ButtonProps } from "@vfuk/core-button/dist/Button.types";
import type { RolesTypes } from "@hub/common-libs";
import { RoleModel } from "@hub/common-libs/build/types/roles.service.model";
import { FormFieldType } from "@hub/common-libs/build/types/customerDetails.service.model";

type RoleFormProps = {
  role: string;
  result: RolesTypes.RoleModel;
  permissionOnActionButton: RolesTypes.Actions | null;
};
type RoleFormType = {
  roleFriendlyName: { pattern: { value: RegExp; message: string } };
  description: { pattern: { value: RegExp; message: string } };
};

interface RoleOption {
  enabled: boolean;
  selected: boolean;
}

const RoleForm = (props: RoleFormProps): JSX.Element => {
  const initialFormFields = {
    roleFriendlyName: { pattern: { value: new RegExp(""), message: "" } },
    description: { pattern: { value: new RegExp(""), message: "" } },
  };
  const addRole = useRoleManagementStore((state) => state.addRole.call);
  const getRoleValidationSchema = useRoleManagementStore(
    (state) => state.fetchRoleValidationSchema.call
  );
  const roleSchema = useRoleManagementStore(
    (state) => state.roleValidationSchema
  );
  const schemaLoadError = useRoleManagementStore(
    (state) => state.fetchRoleValidationSchema.error
  );
  const formHandler = useForm({ mode: "onTouched" });
  const [formFieldRules, setFormFieldsRules] =
    useState<RoleFormType>(initialFormFields);
  const [disableBtn, setDisableBtn] =
    useState<ButtonProps["state"]>("disabled");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const isAddMode = props.role === "add";
  const roleAPIResponse = props.result;
  const [isEditMode, setIsEditMode] = useState(false);
  const { handleSubmit, control, formState, getValues, reset } = formHandler;

  const { errors } = formState;

  useEffect(() => {
    if (formState.isDirty) {
      if (Object.keys(formState.errors).length === 0) setDisableBtn(undefined);
      else setDisableBtn("disabled");
    } else {
      setDisableBtn("disabled");
    }
  }, [formState, schemaLoadError]);

  const navigate = useNavigate();

  // Loading States
  const [isLoadingAdd, setLoadingAdd] = useState(false);
  const [isLoadingEdit, setLoadingEdit] = useState(false);
  const [isLoadingEditRole, setLoadingEditRole] = useState(false);

  const { loading: deleteRoleLoading } = useRoleManagementStore(
    (state) => state.deleteRole
  );

  const [submitState, setSubmitState] = useState(false);
  const [message, setMessage] = useState({
    headerMessage: "",
    subHeaderMessage: "",
  });
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const updateRole = useRoleManagementStore((state) => state.updateRole.call);

  const { call: fetchRoles } = useRoleManagementStore(
    (state) => state.getRoles
  );

  const { t } = useTranslation();
  const [mainMatrix, setMainMatrix] = useState(
    JSON.parse(JSON.stringify(roleAPIResponse.permissionMatrix))
  );

  const handleDeleteClicked = () => {
    setShowDeleteModal(true);
  };
  const refreshRoles = useRoleManagementStore((state) => state.refreshRoles);
  const handleLoading = (isLoading: boolean) => {
    // If we're editing the role, set the appropriate loading state
    if (isEditMode) setLoadingEdit(isLoading);
    else setLoadingAdd(isLoading);
  };
  useEffect(() => {
    if (mainMatrix && mainMatrix.Products) {
      Object.keys(mainMatrix.Products).forEach((productKey) => {
        const product = mainMatrix.Products[productKey];
        Object.keys(product.permissions).forEach((permKey) => {
          const permission = product.permissions[permKey];
          if (permission.display === "End Users") {
            const allFalse = Object.values(permission.options).every(
              (option) => {
                const roleOption = option as RoleOption;
                return roleOption.selected === false;
              }
            );

            if (allFalse) {
              if (permission.options.V) {
                permission.options.V.selected = true;
              }
            }
          }
        });
      });

      setMainMatrix(mainMatrix);
    }
  }, [mainMatrix]);
  const onSubmitRole = async () => {
    setLoadingEditRole(true);
    const collectDirtyFields = Object.keys(formState.touchedFields).map(
      (x: string) => ({
        [x]: getValues(x),
      })
    );
    const updatedFieldsObject = Object.assign({}, ...collectDirtyFields);
    updatedFieldsObject.roleId = roleAPIResponse.roleId;

    await updateRole(updatedFieldsObject)
      .then((res) => {
        setLoadingEditRole(false);
        setMessage({
          headerMessage: res.message || "",
          subHeaderMessage: res.data.message || "",
        });
        const { roleFriendlyName, description } = res.data;

        // Reset the form to new, updated defaults
        reset({
          roleFriendlyName: roleFriendlyName,
          description: description,
        });

        // Exit out of edit mode
        setIsEditMode(false);
        setShowSuccessModal(true);
      })
      .catch((error) => {
        setMessage({
          headerMessage: error.data.message || "",
          subHeaderMessage: (error.data.data && error.data.data.message) || "",
        });
        setShowFailureModal(true);
      })
      .finally(() => {
        setLoadingEditRole(false);
      });
  };

  const onSubmit = handleSubmit(async (data) => {
    handleLoading(true);

    //Handle on submit of permission form
    data.permissionMatrix = { Attributes: {}, Products: {} };
    Object.keys(mainMatrix).map((key) => {
      // get Products and Attributes
      Object.keys(mainMatrix[key]).map((key1) => {
        //Get all IDs of product/attribute matrix
        data.permissionMatrix[key][key1] = {};
        Object.keys(mainMatrix[key][key1].permissions).map((key2) => {
          //get all module under each product/attribites
          Object.keys(mainMatrix[key][key1].permissions[key2].options).map(
            (key3) => {
              //get the permission of that module
              if (
                mainMatrix[key][key1].permissions[key2].options[key3].selected
              ) {
                data.permissionMatrix[key][key1][key2] = key3;
              }
              return 0;
            }
          );
          return 0;
        });
        return 0;
      });
      return 0;
    });

    // Adding a New Role
    if (isAddMode) {
      await addRole(data as RolesTypes.RoleModel)
        .then((res) => {
          setMessage({
            headerMessage: res.message || "",
            subHeaderMessage: res.data.message || "",
          });
          setTimeout(() => {
            setShowSuccessModal(true);
          });
        })
        .catch((error) => {
          setMessage({
            headerMessage: error.data.message || "",
            subHeaderMessage:
              (error.data.data && error.data.data.message) || "",
          });
          setShowFailureModal(true);
        })
        .finally(() => {
          handleLoading(false);
        });
      // Otherwise, Edit the Role
    } else {
      const updatedFieldsObject: RoleModel = {
        roleFriendlyName: data.roleFriendlyName,
        description: data.description,
        roleId: roleAPIResponse.roleId,
        permissionMatrix: data.permissionMatrix,
      };

      await updateRole(updatedFieldsObject)
        .then((res) => {
          setMessage({
            headerMessage: res.message || "",
            subHeaderMessage: res.data.message || "",
          });
          // Reset the form to new, updated defaults
          // Exit out of edit mode
          setIsEditMode(false);
          setShowSuccessModal(true);
        })
        .catch((error) => {
          setMessage({
            headerMessage: error.data.message || "",
            subHeaderMessage:
              (error.data.data && error.data.data.message) || "",
          });
          setShowFailureModal(true);
        })
        .finally(() => {
          handleLoading(false);
        });
    }
  });

  //All validation Starts
  useEffect(() => {
    getRoleValidationSchema();
  }, [getRoleValidationSchema]);

  useEffect(() => {
    const formFields = ["roleFriendlyName", "description"];
    const rules: RoleFormType | [] = [];

    formFields.forEach((element: FormFieldType["fieldName"]) => {
      const fieldExists = roleSchema.find(
        (item: FormFieldType) => item.fieldName === element
      ) as unknown as FormFieldType;

      if (fieldExists) {
        const fieldRules = {
          required: {
            value: fieldExists.required || false,
            message:
              fieldExists.requiredErrorMessage || element + " is not required",
          },
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: Not sure what this code does, but produces a TS Error
        rules[element] = fieldRules;
      } else {
        const fieldRules = {
          required: {
            value: false,
            message: element + " is not required",
          },
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: Not sure what this code does, but produces a TS Error
        rules[element] = fieldRules;
      }
    });
    setFormFieldsRules(rules as unknown as RoleFormType);
  }, [roleSchema]);

  //handler for adding pattern rule out of array on change #AMIT
  const handlerForRulePatterns = (
    fieldName: keyof RoleFormType,
    value: string
  ) => {
    let shouldSkip = false;
    const fieldExists = roleSchema.find(
      (item: { fieldName: string }) => item.fieldName === fieldName
    ) as unknown as FormFieldType;

    if (fieldExists) {
      const fieldsValidation = fieldExists.validation;

      if (fieldsValidation && fieldsValidation.length > 0) {
        fieldsValidation.forEach((element) => {
          if (element.pattern) {
            if (shouldSkip) {
              return;
            }
            setDisableBtn(undefined);
            if (!new RegExp(element.pattern).test(value)) {
              setDisableBtn("disabled");
              shouldSkip = true;
              formFieldRules[fieldName].pattern = {
                value: new RegExp(element.pattern),
                message: element.errorMessage,
              };
            }
          }
        });
      }
    }
  };

  const changePermission = (
    //On click of permission matrix main permission form will be over-write
    returnMatrix: object,
    matrixType: string
  ) => {
    mainMatrix[matrixType] = returnMatrix;
    setMainMatrix(JSON.parse(JSON.stringify(mainMatrix)));
    setSubmitState(true); //Activate submit button when newpermission is set
  };

  return (
    <div data-testid="roleForm">
      <Modal
        srName={"roleCreated"}
        isOpen={showSuccessModal}
        size={2}
        onClose={() => {
          setShowSuccessModal(false);
          refreshRoles();
          navigate("/roles");
        }}
      >
        <SuccessModalContent
          heading={message.headerMessage}
          body={message.subHeaderMessage}
          confirmText={t("core:roles:back_to_roles")}
          onConfirm={() => {
            setShowSuccessModal(false); // Hide the modal on 'Back' click
            navigate("/roles");
            refreshRoles();
          }}
        />
      </Modal>
      <Modal
        srName={"roleFailedToCreate"}
        isOpen={showFailureModal}
        size={2}
        onClose={() => setShowFailureModal(false)}
      >
        <FailureModalContent
          heading=""
          message={
            message.headerMessage || t("core:roles:failed_to_create_role")
          }
          body={message.subHeaderMessage}
          errorMessage={message.subHeaderMessage || ""}
          closeMessage={t("core:common:okay_txt") || "Okay"}
          onClose={() => setShowFailureModal(false)}
          loading={deleteRoleLoading}
        />
      </Modal>

      <SimpleGrid
        layout={{
          sm: [12],
          lg: [2, 10],
        }}
        spacing={3}
      >
        {isAddMode ? (
          <Heading level={5} weight={3}>
            {t("core:roles:add")}
          </Heading>
        ) : (
          <LinkWithIcon
            appearance="secondary"
            text={t("core:roles:back")}
            icon={{
              name: "chevron-left",
              justify: "left",
            }}
            customRouterProps={{ to: "/roles" }}
          />
        )}
        <Heading level={5} weight={3}>
          <div className={"rightAlign"}>{t("core:roles:no_access")}</div>
          <span className={"SmallGray F"}>N</span>
          <div className={"rightAlign"}>{t("core:roles:view_access")}</div>
          <span className={"SmallGray F"}>V</span>
          <div className={"rightAlign"}>{t("core:roles:edit_access")}</div>
          <span className={"SmallGray F"}>E</span>
          <div className={"rightAlign"}>{t("core:roles:full_access")}</div>{" "}
          <span className={"SmallGray F"}>F</span>
        </Heading>
      </SimpleGrid>

      <Spacing
        spacingLevel={{
          top: 7,
          bottom: 0,
        }}
      >
        <form id="roleForm" onSubmit={onSubmit}>
          <div className={isAddMode ? "addBackBanner" : "editBackBanner"}>
            <SimpleGrid
              layout={{
                sm: [12],
                lg: isAddMode ? [4, 8] : [4, 6, 2],
              }}
              spacing={3}
              verticalAlign="top"
            >
              {(isAddMode || isEditMode) && (
                <Controller
                  name="roleFriendlyName"
                  control={control}
                  rules={formFieldRules.roleFriendlyName}
                  defaultValue=""
                  render={({ field }) => (
                    <FloatingLabelInput
                      label={t("core:roles:name")}
                      error={
                        errors.roleFriendlyName
                          ? (errors.roleFriendlyName.message as string)
                          : ""
                      }
                      type="text"
                      id="roleFriendlyName"
                      state={errors.roleFriendlyName ? "error" : undefined}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handlerForRulePatterns(
                          "roleFriendlyName",
                          e.target.value
                        );
                      }}
                    />
                  )}
                />
              )}
              {(isAddMode || isEditMode) && (
                <Controller
                  name="description"
                  control={control}
                  rules={formFieldRules.description}
                  defaultValue=""
                  render={({ field }) => (
                    <FloatingLabelInput
                      label={t("core:roles:description")}
                      error={
                        errors.description
                          ? (errors.description.message as string)
                          : ""
                      }
                      type="text"
                      id="description"
                      state={errors.description ? "error" : undefined}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handlerForRulePatterns("description", e.target.value);
                      }}
                    />
                  )}
                />
              )}
              {!isAddMode && !isEditMode && (
                <>
                  {t("core:roles:name")}
                  <Heading level={5} weight={3}>
                    {roleAPIResponse.roleFriendlyName}
                  </Heading>
                </>
              )}
              {!isAddMode && !isEditMode && (
                <>
                  {t("core:roles:description")}
                  <Heading level={5} weight={1}>
                    {roleAPIResponse.description}
                  </Heading>
                </>
              )}
              {!isAddMode && isEditMode && (
                <div className="designedClass">
                  <Spacing
                    spacingLevel={{
                      top: 3,
                    }}
                  >
                    <SimpleGrid
                      columns={{
                        sm: 2,
                        md: 2,
                      }}
                      verticalAlign="bottom"
                    >
                      <SmallButton
                        text={t("core:roles:cancel")}
                        appearance="secondary"
                        state={
                          isLoadingEdit || isLoadingAdd ? "disabled" : undefined
                        }
                        inverse
                        onClick={() => {
                          reset({
                            roleFriendlyName: roleAPIResponse.roleFriendlyName,
                            description: roleAPIResponse.description,
                          });
                          setIsEditMode(false);
                        }}
                      />
                      <SmallButton
                        text={t("core:roles:edit")}
                        appearance="primary"
                        state={disableBtn || undefined}
                        loading={isLoadingEditRole}
                        onClick={() => {
                          formHandler.handleSubmit(onSubmitRole)();
                        }}
                      />
                    </SimpleGrid>
                  </Spacing>
                </div>
              )}
              {props.permissionOnActionButton &&
                props.permissionOnActionButton.edit &&
                !isAddMode &&
                !isEditMode && (
                  <div className="designedClass">
                    <Button
                      dataAttributes={{ "data-testid": "editRole" }}
                      text={props.permissionOnActionButton.edit.label}
                      appearance="secondary"
                      onClick={() => {
                        reset({
                          roleFriendlyName: roleAPIResponse.roleFriendlyName,
                          description: roleAPIResponse.description,
                        });
                        setIsEditMode(true);
                      }}
                    />
                  </div>
                )}
            </SimpleGrid>
          </div>
          <Spacing
            spacingLevel={{
              top: 7,
              bottom: 4,
            }}
          >
            <Heading level={4} weight={3}>
              {t("core:roles:products")}
            </Heading>
          </Spacing>
          <SimpleGrid
            columns={{
              sm: 1,
            }}
            spacing={2}
          >
            {mainMatrix.Products && (
              <AccordionBox
                matrix={mainMatrix.Products}
                matrixType="Products"
                handlePermissionChanges={changePermission}
              />
            )}
          </SimpleGrid>
          <Spacing
            spacingLevel={{
              top: 7,
              bottom: 7,
            }}
          >
            <Heading level={4} weight={3}>
              {t("core:roles:attributes")}
            </Heading>
            <SimpleGrid
              columns={{
                sm: 1,
              }}
              spacing={2}
            >
              {mainMatrix.Attributes && (
                <AccordionBox
                  matrix={mainMatrix.Attributes}
                  matrixType="Attributes"
                  handlePermissionChanges={changePermission}
                />
              )}
            </SimpleGrid>
          </Spacing>
          <SimpleGrid
            layout={{
              sm: [12],
              lg: isAddMode ? [12] : [4, 8],
            }}
            spacing={3}
          >
            <div>
              {props.permissionOnActionButton &&
                props.permissionOnActionButton.delete &&
                !isAddMode && (
                  <Button
                    text={props.permissionOnActionButton.delete.label}
                    appearance="alt1"
                    onClick={handleDeleteClicked}
                    loading={deleteRoleLoading}
                  />
                )}
            </div>

            <div className={"rightAlign"}>
              {props.permissionOnActionButton &&
                ((props.permissionOnActionButton.edit && !isAddMode) ||
                  (props.permissionOnActionButton.add && isAddMode)) && (
                  <>
                    <Button
                      text={t("core:roles:cancel")}
                      appearance="alt1"
                      state={
                        isLoadingEdit || isLoadingAdd ? "disabled" : undefined
                      }
                      onClick={() => {
                        navigate("/roles");
                        fetchRoles(true);
                      }}
                    />
                    &nbsp; &nbsp;
                    {
                      <Button
                        onClick={onSubmit}
                        text={
                          isAddMode
                            ? props.permissionOnActionButton.add
                              ? props.permissionOnActionButton.add.label
                              : t("core:roles:add")
                            : t("core:roles:edit")
                        }
                        appearance="primary"
                        loading={isLoadingAdd || isLoadingEdit}
                        state={submitState ? undefined : "disabled"}
                      />
                    }
                  </>
                )}
            </div>
          </SimpleGrid>
        </form>
      </Spacing>
      <DeleteRoleModal
        show={showDeleteModal}
        roleData={roleAPIResponse}
        isLinked={true}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        reloadList={() => {
          fetchRoles(true);
        }}
      />
    </div>
  );
};
export default RoleForm;
